import {
  BridgeManager,
  EdgePathCropper,
  GeneralPath,
  type IEdge,
  type IPoint,
  type IRenderContext,
  PathType,
  Point,
} from '@ardoq/yfiles';
import { MARKER_PROPS } from '@ardoq/icons';
import { ArdoqId, ArrowType } from '@ardoq/api-types';
import {
  GraphEdgeBase,
  GraphItem,
  isAggregatedGraphEdge,
  isParentChildGraphEdge,
  REFERENCE_PATH_STROKE_WIDTH,
} from '@ardoq/graph';
import { Edge as OldGraphEdge } from 'graph/edge';
import type { Point as MathPoint } from '@ardoq/math';
import type { PathPiece } from 'tabview/canvasRendering/anchoredDashes';

const getMarkerOffset = (lineCap: ArrowType, isEndPoint: boolean) => {
  // although the typing indicates MARKER_PROPS[lineCap] will be non-null, error logs indicate otherwise. to be extra safe, coerce nulls to 0
  const refX = MARKER_PROPS[lineCap]?.refX ?? 0;
  if (!isEndPoint) {
    return REFERENCE_PATH_STROKE_WIDTH * refX;
  }
  const markerWidth = MARKER_PROPS[lineCap]?.markerWidth ?? 0;
  return REFERENCE_PATH_STROKE_WIDTH * (markerWidth - refX);
};

const offsetEndPoint = (
  point: IPoint,
  nextPoint: IPoint,
  length: number,
  lineCap: ArrowType,
  isEndPoint: boolean
) => {
  if (!length) {
    return point;
  }

  const offset = getMarkerOffset(lineCap, isEndPoint);
  const { x: x1, y: y1 } = point;
  const { x: x2, y: y2 } = nextPoint;
  return new Point(
    x1 - ((x1 - x2) / length) * offset,
    y1 - ((y1 - y2) / length) * offset
  );
};
const edgePathCropper = new EdgePathCropper();
const cropPathAtSourceAndTarget = (edge: IEdge, path: GeneralPath) =>
  edgePathCropper.cropEdgePath(
    edge,
    true,
    null,
    edgePathCropper.cropEdgePath(edge, false, null, path)
  );

export const createPath = (edge: IEdge, isHover: boolean) => {
  const { bends } = edge;
  const bendLocations: IPoint[] = [];
  for (const { location } of bends) {
    const { x, y } = location;
    const lastLocation = bendLocations.at(-1);
    if (y !== lastLocation?.y || x !== lastLocation?.x) {
      bendLocations.push(location);
    }
  }
  const locations = [
    edge.sourcePort!.location,
    ...bendLocations,
    edge.targetPort!.location,
  ];
  const [startPort] = locations;
  const endPort = locations.at(-1)!;

  const secondPoint = bendLocations.length ? bendLocations[0] : endPort;
  const startLength = startPort.distanceTo(secondPoint);

  const graphEdge = edge.tag as GraphEdgeBase | OldGraphEdge;

  const pathStart = isHover
    ? startPort
    : offsetEndPoint(
        startPort,
        secondPoint,
        startLength,
        graphEdge.getLineBeginning(),
        false
      );

  const penultimatePoint =
    locations.length <= 2 ? locations[0] : locations.at(-2)!;
  const endLength = penultimatePoint.distanceTo(endPort);

  const pathEnd = isHover
    ? endPort
    : offsetEndPoint(
        endPort,
        penultimatePoint,
        endLength,
        graphEdge.getLineEnding(),
        true
      );

  const path = new GeneralPath();
  path.moveTo(pathStart);
  for (const middlePoint of locations.slice(1, -1)) {
    path.lineTo(middlePoint);
  }
  path.lineTo(pathEnd);

  return cropPathAtSourceAndTarget(edge, path);
};
export const createPathWithBridges = (
  path: GeneralPath,
  renderContext: IRenderContext
) => {
  const manager = getBridgeManager(renderContext);
  return manager ? manager.addBridges(renderContext, path) : path;
};

const getBridgeManager = (renderContext: IRenderContext) => {
  const bridgeManager = renderContext.lookup(BridgeManager);
  return bridgeManager instanceof BridgeManager && bridgeManager;
};
export const edgeModelIds = (edge: IEdge): ArdoqId[] =>
  isParentChildGraphEdge(edge.tag)
    ? []
    : isAggregatedGraphEdge(edge.tag)
      ? edge.tag.modelIds
      : edge.tag instanceof GraphItem
        ? [edge.tag.modelId]
        : [(edge.tag as OldGraphEdge).dataModel.id];

export const toPathPieces = (path: GeneralPath): PathPiece[] => {
  const cursor = path.createCursor();
  const coords = [NaN, NaN, NaN, NaN];
  const pieces: PathPiece[] = [];
  let current: MathPoint = [NaN, NaN];
  while (cursor.moveNext()) {
    const pathType = cursor.getCurrent(coords);
    const [x1, y1, x2, y2] = coords;
    switch (pathType) {
      case PathType.MOVE_TO:
        current = [x1, y1];
        break;
      case PathType.LINE_TO:
        pieces.push({ type: 'line', from: current, to: [x1, y1] });
        current = [x1, y1];
        break;
      case PathType.QUAD_TO:
        pieces.push({
          type: 'curve',
          from: current,
          control: [x1, y1],
          to: [x2, y2],
        });
        current = [x2, y2];
        break;
    }
  }
  return pieces;
};
